"use client";

import { useState } from "react";
import { Input, Button, Select, Checkbox } from "rizzui";
import { Controller, useForm } from "react-hook-form";
import FormGroup from "@/app/shared/form-group";
import FormFooter from "@core/components/form-footer";

interface Currency {
  label: string;
  value: string;
  feeType: "percentage" | "base_fee";
  feeRate: string;
  transactionMode: "payment" | "payout";
  minWithdraw: string;
  exchangeRate: string;

  // interface Currency {
  //   label: string;
  //   value: string;
  //   feeType: "percentage" | "base_fee";
  //   feeRate: string;
  //   payment: boolean;
  //   payout: boolean;
  //   minWithdraw: string;
  //   exchangeRate: string;
}

const defaultMinimusWithdrawal: Record<string, number> = {
  USD: 3.57,
  NGN: 5000,
  GBP: 2.78,
  EUR: 3.12,
  CAD: 4.55,
  CNY: 25.0,
  GHS: 45.45,
  AED: 12.5,
};

const availableCurrencies = [
  { label: "USD - US Dollar", value: "USD" },
  { label: "NGN - Nigerian Naira", value: "NGN" },
  { label: "EUR - Euro", value: "EUR" },
  { label: "GBP - British Pound", value: "GBP" },
  { label: "CAD - Canadian Dollar", value: "CAD" },
  { label: "CNY - Chinese Yuan", value: "CNY" },
  { label: "GHS - Ghanaian Cedi", value: "GHS" },
  { label: "AED - UAE Dirham", value: "AED" },
];

const feeTypes = [
  { label: "Percentage", value: "percentage" },
  { label: "Base Fee", value: "base_fee" },
];

export default function FxRateFeesSettings() {
  const { control, handleSubmit } = useForm();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedMode, setSelectedMode] = useState<"payment" | "payout" | null>(
    null
  );

  const addCurrency = (
    currency: { label: string; value: string },
    selectedMode: "payment" | "payout" | null
  ) => {
    if(!selectedMode) return;
    if (
      !currencies.find(
        (c) => c.value === currency.value && c.transactionMode === selectedMode
      )
    ) {
      setCurrencies([
        ...currencies,
        {
          ...currency,
          feeType: "percentage",
          feeRate: "",
          transactionMode: selectedMode,
          minWithdraw: defaultMinimusWithdrawal[currency.value].toString(),
          exchangeRate: "0",
        },
      ]);
    }
    setSelectedCurrency(null);
  };

  const removeCurrency = (
    currency: { label: string; value: string },
    selectedMode: "payment" | "payout" | null
  ) => {
    if (!currency || !selectedMode) return;

    setCurrencies(
      currencies.filter(
        (c) =>
          !(c.value === currency.value && c.transactionMode === selectedMode)
      )
    );

    setSelectedCurrency(null);
  };

  const updateCurrency = <T extends keyof Currency>(
    index: number,
    field: T,
    value: Currency[T]
  ) => {
    setCurrencies((prevCurrencies) =>
      prevCurrencies.map((currency, i) =>
        i === index ? { ...currency, [field]: value } : currency
      )
    );
  };

  return (
    <>
      <FormGroup
        title="FX Rate & Fees Settings"
        description="Update FX Rate & Fees Settings"
        className="pt-7 flex justify-between"
      >
        <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-4 sm:col-span-full">
          <Select
            options={availableCurrencies}
            value={selectedCurrency}
            onChange={setSelectedCurrency}
            placeholder="Select Currency"
            className="sm:w-[200px] flex-nowrap text-nowrap"
          />
          <Select
            options={[
              { label: "Payout", value: "payout" },
              { label: "Payment", value: "payment" },
            ]}
            value={selectedMode}
            onChange={(val: { label: string; value: "payment" | "payout" }) =>
              setSelectedMode(val.value)
            }
            className="sm:w-[150px]"
            placeholder="Select Mode"
          />
          <Button
            onClick={() =>
              selectedCurrency && addCurrency(selectedCurrency, selectedMode)
            }
            className="text-nowrap sm:w-fit w-full"
          >
            Add Currency
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              selectedCurrency && removeCurrency(selectedCurrency, selectedMode)
            }
            disabled={!selectedCurrency || !selectedMode}
            className="text-nowrap sm:w-fit w-full"
          >
            Remove Currency
          </Button>
        </div>
      </FormGroup>

      <div className="grid gap-4 mt-7">
        {currencies.map((currency, index) => (
          <div
            key={currency.value}
            className="border-b py-7 @2xl:py-9 @3xl:py-11 rounded-lg grid gap-8 col-span-full"
          >
            <div>
              <h4 className="text-lg font-semibold">
                {currency.label} ({currency.transactionMode})
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-5">
              <div className="flex gap-2 items-center">
                <label htmlFor="charge-type" className="text-nowrap">
                  Charge Type:
                </label>
                <Select
                  name="charge-type"
                  id={"charge-type"}
                  options={feeTypes}
                  value={currency.feeType}
                  onChange={(value: "percentage" | "base_fee") =>
                    updateCurrency(index, "feeType", value)
                  }
                />
              </div>

              <div className="flex gap-2 items-center">
                <label htmlFor="fee-rate" className="text-nowrap">
                  Fee/Rate:
                </label>
                <Input
                  type="number"
                  name="fee-rate"
                  id="fee-rate"
                  value={currency.feeRate}
                  onChange={(e) =>
                    updateCurrency(index, "feeRate", e.target.value)
                  }
                  placeholder="Enter Fee/Rate"
                />
              </div>

              <div className="flex gap-2 items-center">
                <label htmlFor="min-withdraw" className="text-nowrap">
                  Min Withdrawal:
                </label>
                <Input
                  type="number"
                  name="min-withdraw"
                  id="min-withdraw"
                  value={currency.minWithdraw}
                  onChange={(e) =>
                    updateCurrency(index, "minWithdraw", e.target.value)
                  }
                  placeholder="Enter minimum withdrawal amount"
                />
              </div>
              <div className="flex gap-2 items-center">
                <label htmlFor="fee-rate" className="text-nowrap">
                  Exchange Rate:
                </label>
                <Input
                  type="number"
                  name="exchnage-rate"
                  id="exchange-rate"
                  value={currency.exchangeRate}
                  onChange={(e) =>
                    updateCurrency(index, "exchangeRate", e.target.value)
                  }
                  placeholder="Enter Currency Exchange Rate"
                />
              </div>
            </div>
            {/* <div className="flex gap-4">
              <div className="flex gap-2 items-center">
                <label htmlFor="fee-rate" className="text-nowrap">
                  Exchange Rate:
                </label>
                <Input
                  type="number"
                  name="exchnage-rate"
                  id="exchange-rate"
                  value={currency.minWithdraw}
                  onChange={(e) =>
                    updateCurrency(index, "exchangeRate", e.target.value)
                  }
                  placeholder="Enter Currency Exchange Rate"
                />
              </div>
              <Checkbox
                label="Payment"
                checked={currency.payment}
                onChange={(e) =>
                  updateCurrency(index, "payment", e.target.checked)
                }
              />
              <Checkbox
                label="Payout"
                checked={currency.payout}
                onChange={(e) =>
                  updateCurrency(index, "payout", e.target.checked)
                }
              />
            </div> */}
          </div>
        ))}
        <FormFooter
          // isLoading={isLoading}
          currencies={currencies}
          altBtnText="Cancel"
          submitBtnText="Update"
        />
      </div>
    </>
  );
}
